/**
 * Who looks like what.
 *
 * A person's photo is one picture across the website, the messenger and the
 * phone (hrms `profile_photo_url`; the messenger writes its own uploads back
 * here). Most HR rows don't carry it, so the directory answers "what does this
 * user id look like" once for every list on the page.
 *
 * Mirrors the messenger's src/utils/people/peopleDirectory.ts so a row looks
 * the same in both apps — the only difference is where the photos come from
 * (there, the chat service; here, /employeesAll).
 */
const PHOTO_URL = /^(https?:\/\/|data:image\/)/i;

/** A drawable photo URL, or null for blank/relative/junk values. */
export function photoUrlOrNull(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return PHOTO_URL.test(trimmed) ? trimmed : null;
}

function idOrNull(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value !== "string") return null;
  return value.trim() || null;
}

function codeKey(value: unknown): string | null {
  return idOrNull(value)?.toUpperCase() ?? null;
}

export type EmployeeIdentity = {
  employeeCode: string | null;
  employeeId: string;
  photoUrl: string | null;
  userId: string | null;
};

export type EmployeeHints = { employeeCode?: string | null; employeeId?: string | null };
export type EmployeeIdentityIndex = { resolve: (hints: EmployeeHints) => EmployeeIdentity | null };

export const EMPTY_EMPLOYEE_INDEX: EmployeeIdentityIndex = { resolve: () => null };

/** user id → photo, from the same employee rows the index below is built on. */
export function buildPhotoDirectory(
  people: readonly EmployeeIdentity[] | null | undefined,
  self?: { photoUrl?: string | null; id?: string | null } | null,
): ReadonlyMap<string, string> {
  const photos = new Map<string, string>();
  for (const person of people ?? []) {
    const userId = idOrNull(person.userId);
    const photo = photoUrlOrNull(person.photoUrl);
    if (userId && photo) photos.set(userId, photo);
  }
  const selfId = idOrNull(self?.id);
  if (selfId) {
    // /employeesAll leaves the caller's own row out, and their own photo
    // changes the moment they set it on My Profile — so it comes from the
    // signed-in user instead, and a cleared photo deletes the stale entry.
    const own = photoUrlOrNull(self?.photoUrl);
    if (own) photos.set(selfId, own);
    else photos.delete(selfId);
  }
  return photos;
}

/** HR rows that only know `Employee.id` (emptID) or an employee code → the person. */
export function buildEmployeeIdentityIndex(
  identities: readonly EmployeeIdentity[] | null | undefined,
): EmployeeIdentityIndex {
  if (!identities?.length) return EMPTY_EMPLOYEE_INDEX;
  const byId = new Map<string, EmployeeIdentity>();
  const byCode = new Map<string, EmployeeIdentity | null>();
  for (const identity of identities) {
    byId.set(identity.employeeId, identity);
    const code = codeKey(identity.employeeCode);
    if (!code) continue;
    // Employee codes are not unique in hrms. A code two people share resolves
    // to nobody: initials beat someone else's face.
    const existing = byCode.get(code);
    byCode.set(code, byCode.has(code) && existing?.employeeId !== identity.employeeId ? null : identity);
  }
  return {
    resolve({ employeeCode, employeeId }) {
      const id = idOrNull(employeeId);
      const found = id ? byId.get(id) : undefined;
      if (found) return found;
      // A supplied-but-unknown employee id falls through to the code, so rows
      // whose "employeeId" is really a code still resolve; an ambiguous code
      // is already null above, so this can't show the wrong face.
      const code = codeKey(employeeCode);
      return code ? byCode.get(code) ?? null : null;
    },
  };
}

/**
 * "Asha Mehta" → "AM". Used when there is no photo to draw.
 *
 * First letter of the first two words, character for character the messenger's
 * utils/chat/format.getInitials — the same person must not be "AM" in one app
 * and "AS" in the other.
 */
export function getInitials(name?: string | null): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts.slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
}
